import { useLocalObservable } from 'mobx-react-lite';

export interface IRoomCallPreviewVideoAudioSourcePresenter {
  audioContext: AudioContext | null;
  audioSource: MediaStreamAudioSourceNode | null;
  intervalId: ReturnType<typeof setInterval> | null;
  analyser: AnalyserNode | null;
  isSpeaking: boolean;

  checkAudioLevel(stream: MediaStream): Promise<void>;
  stopCheckAudioLevel(): void;
}

export const useRoomCallAudioSourcePreviewVideoPresenter =
  (): IRoomCallPreviewVideoAudioSourcePresenter => {
    const presenter = useLocalObservable<IRoomCallPreviewVideoAudioSourcePresenter>(() => ({
      audioContext: null,
      audioSource: null,
      intervalId: null,
      analyser: null,
      isSpeaking: false,

      async checkAudioLevel(audioStream: MediaStream): Promise<void> {
        presenter.audioContext = new AudioContext();
        presenter.audioSource = presenter.audioContext.createMediaStreamSource(audioStream);
        presenter.analyser = presenter.audioContext.createAnalyser();
        presenter.analyser.fftSize = 512;
        presenter.analyser.minDecibels = -127;
        presenter.analyser.maxDecibels = 0;
        presenter.analyser.smoothingTimeConstant = 0.4;
        presenter.audioSource.connect(presenter.analyser);
        const volumes = new Uint8Array(presenter.analyser.frequencyBinCount);
        const volumeCallback = (): void => {
          if (!presenter.analyser) {
            return;
          }
          presenter.analyser.getByteFrequencyData(volumes);
          let volumeSum = 0;
          for (const volume of volumes) volumeSum += volume;
          const averageVolume = volumeSum / volumes.length;
          // Value range: 127 = analyser.maxDecibels - analyser.minDecibels;
          const volumeVal = (averageVolume * 100) / 127;
          if (volumeVal > 15 && !presenter.isSpeaking) {
            presenter.isSpeaking = true;
          } else if (volumeVal <= 15 && presenter.isSpeaking) {
            presenter.isSpeaking = false;
          }
        };
        setInterval(volumeCallback, 100);
      },

      stopCheckAudioLevel(): void {
        if (presenter.intervalId) {
          clearInterval(presenter.intervalId);
          presenter.intervalId = null;
        }

        if (presenter.audioSource) {
          presenter.audioSource.disconnect();
          presenter.audioSource = null;
        }
        presenter.analyser = null;
        presenter.audioContext = null;
        presenter.isSpeaking = false;
      },
    }));

    return presenter;
  };
